import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const EVENT_TYPES = {
  es: ["Cumpleaños", "Baby Shower", "Quinceañera", "Boda", "Aniversario", "Reunión / Corporativo", "Otro"],
  en: ["Birthday", "Baby Shower", "Sweet Fifteen", "Wedding", "Anniversary", "Meeting / Corporate", "Other"],
};

export const ReservationForm = ({ settings }) => {
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", event_type: "", guests: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const T = lang === "es"
    ? { title: "Solicita tu reserva", name: "Nombre", phone: "Teléfono", email: "Correo (opcional)", date: "Fecha del evento", type: "Tipo de evento", guests: "Número de invitados", msg: "Mensaje (opcional)", send: "Enviar solicitud", choose: "Selecciona…", okTitle: "¡Solicitud enviada!", okText: "Gracias, te contactaremos pronto para confirmar tu reserva.", again: "Enviar otra", err: "Completa nombre y teléfono" }
    : { title: "Request your booking", name: "Name", phone: "Phone", email: "Email (optional)", date: "Event date", type: "Event type", guests: "Number of guests", msg: "Message (optional)", send: "Send request", choose: "Select…", okTitle: "Request sent!", okText: "Thank you, we'll contact you soon to confirm your booking.", again: "Send another", err: "Please add name and phone" };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(T.err);
      return;
    }
    setBusy(true);
    try {
      await api.post("/reservations", form);
      setDone(true);
      setForm({ name: "", phone: "", email: "", date: "", event_type: "", guests: "", message: "" });
    } catch {
      toast.error(lang === "es" ? "Error al enviar. Intenta de nuevo." : "Error sending. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1.5 w-full bg-dough/60 border border-ink/15 rounded-xl px-4 py-3 text-sm text-ink font-body focus:border-terracotta focus:bg-white transition-colors";
  const labelCls = "font-label text-xs font-bold tracking-wide text-ink/60 uppercase";

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl border border-ink/10 p-10 text-center shadow-2xl shadow-ink/10"
        data-testid="reservation-success"
      >
        <CheckCircle2 className="text-quetzal mx-auto mb-4" size={44} />
        <h3 className="font-serif text-3xl text-ink mb-2">{T.okTitle}</h3>
        <p className="font-body text-ink/70 max-w-sm mx-auto">{T.okText}</p>
        <button onClick={() => setDone(false)} className="mt-6 font-label font-bold text-terracotta link-underline" data-testid="reservation-again">
          {T.again}
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-ink/10 p-6 md:p-8 shadow-2xl shadow-ink/10" data-testid="reservation-form">
      <h3 className="font-serif text-2xl md:text-3xl text-ink mb-6">{T.title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>{T.name}</span>
          <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="res-name" />
        </label>
        <label className="block">
          <span className={labelCls}>{T.phone}</span>
          <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="res-phone" />
        </label>
        <label className="block">
          <span className={labelCls}>{T.date}</span>
          <input type="date" className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="res-date" />
        </label>
        <label className="block">
          <span className={labelCls}>{T.guests}</span>
          <input type="number" min="1" className={inputCls} value={form.guests} onChange={(e) => set("guests", e.target.value)} data-testid="res-guests" />
        </label>
        <label className="block sm:col-span-2">
          <span className={labelCls}>{T.type}</span>
          <select className={inputCls} value={form.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="res-type">
            <option value="">{T.choose}</option>
            {EVENT_TYPES[lang].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="block sm:col-span-2">
          <span className={labelCls}>{T.email}</span>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="res-email" />
        </label>
        <label className="block sm:col-span-2">
          <span className={labelCls}>{T.msg}</span>
          <textarea className={inputCls + " min-h-[90px]"} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="res-message" />
        </label>
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="res-submit"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-terracotta text-dough font-label font-bold rounded-full py-4 hover:bg-ink transition-colors disabled:opacity-60"
      >
        {busy && <Loader2 className="animate-spin" size={16} />} {T.send}
      </button>
    </form>
  );
};
